import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, INTERESTS } from "@/lib/api";
import { ReferralBox } from "./ReferralBox";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^(\+91|91|0)?[6-9]\d{9}$/;

export const WaitlistForm = ({ source }) => {
  const [form, setForm] = useState({ email: "", mobile: "", interest: "" });
  const [status, setStatus] = useState({ type: "idle", msg: "", position: null, code: null });
  const [ref] = useState(() => new URLSearchParams(window.location.search).get("ref"));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const p = `waitlist-${source}`;

  const submit = async (e) => {
    e.preventDefault();
    const mobile = form.mobile.replace(/[\s-]/g, "");
    if (!EMAIL_RE.test(form.email)) return setStatus({ type: "error", msg: "Enter a valid email address" });
    if (!MOBILE_RE.test(mobile)) return setStatus({ type: "error", msg: "Enter a valid 10-digit Indian mobile number" });
    if (!form.interest) return setStatus({ type: "error", msg: "Select what you're most interested in" });
    setStatus({ type: "loading", msg: "" });
    try {
      const { data } = await api.post("/waitlist", { ...form, mobile, source, ref: ref || undefined });
      setStatus({ type: "success", msg: "We've emailed your confirmation and will send your early-access invite before the beta opens.", position: data.position, code: data.ref_code });
      toast.success("You're on the Trading Nifty waitlist");
      setForm({ email: "", mobile: "", interest: "" });
    } catch (err) {
      setStatus({ type: "error", msg: errorMessage(err) });
    }
  };

  if (status.type === "success") {
    return (
      <div data-testid={`${p}-success`} className="flex items-start gap-3 min-w-0 rounded-lg border border-[rgba(163,230,53,0.35)] bg-[rgba(163,230,53,0.06)] p-5">
        <CheckCircle2 className="hidden sm:block text-[var(--tn-green)] shrink-0 mt-0.5" size={20} />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--tn-green)]">You're on the list</p>
          <p className="font-display text-3xl font-semibold mt-1" data-testid={`${p}-position`}>You're #{status.position}</p>
          <p className="text-sm text-[var(--tn-muted)] mt-2">{status.msg}</p>
          <ReferralBox code={status.code} testPrefix={p} />
          <button onClick={() => setStatus({ type: "idle", msg: "" })} data-testid={`${p}-add-another`} className="text-sm text-[var(--tn-cyan)] mt-2 hover:underline">
            Add another email
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid={`${p}-form`} className="space-y-4">
      {ref && <p className="text-xs font-mono text-[var(--tn-green)]" data-testid={`${p}-referred-note`}>You were invited by a friend — joining gives them a boost.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-[var(--tn-muted)]">Email address *</span>
          <input type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com"
            className="tn-input mt-1.5" data-testid={`${p}-email-input`} />
        </label>
        <label className="block">
          <span className="text-xs text-[var(--tn-muted)]">Mobile number *</span>
          <input type="tel" required value={form.mobile} onChange={set("mobile")} placeholder="98765 43210"
            className="tn-input mt-1.5" data-testid={`${p}-mobile-input`} />
        </label>
      </div>
      <label className="block">
        <span className="text-xs text-[var(--tn-muted)]">What are you most interested in? *</span>
        <select required value={form.interest} onChange={set("interest")} className="tn-input mt-1.5 cursor-pointer" data-testid={`${p}-interest-select`}>
          <option value="" disabled>Select an option</option>
          {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </label>
      {status.type === "error" && (
        <p data-testid={`${p}-error`} className="text-sm text-[var(--tn-red)]">{status.msg}</p>
      )}
      <button type="submit" className="tn-btn w-full sm:w-auto h-11" disabled={status.type === "loading"} data-testid={`${p}-submit-btn`}>
        {status.type === "loading" ? <Loader2 className="animate-spin" size={16} /> : null}
        Get early access <ArrowRight size={16} />
      </button>
      <p className="text-xs text-[var(--tn-muted)]">Fields marked * are required. Early-access invites only — we never share your number.</p>
    </form>
  );
};
